import http, { endpoints } from './http';
/**
 * Known gaps — Efkt capabilities the backend has not shipped yet.
 *
 * **Grep `EFKT-BE-GAP` across this package and `apps/booqly/v2/` for every one of them.** Each site
 * carries its own note saying exactly what is missing, who owns it, and what happens today.
 *
 * The rule at every one of those sites is the same, and it is deliberate: **once a record's owning
 * backend resolves to Efkt, the Efkt path is still taken.** The call is made, it fails against the
 * missing route, and the failure surfaces. Nothing silently reroutes an Efkt-owned record to datahub —
 * that would write to a backend that does not own the row, and it would hide the gap from exactly the
 * people who need to see it. A gap is a 404 in the network tab, not a quiet fallback.
 *
 * Current list:
 *
 * - **Feedback / complaints** (`./orders` `submitFeedback`, `./orders/dtos.ts`, `./orders/helpers.ts`).
 *   No feedback capability exists anywhere in Efkt — not on the fe-gateway, not in
 *   `Efkt/Applications/Api/**`, not in the contracts. `POST /api/order-line/{orderLineId}/feedback` is
 *   our assumed route, its body shaped from datahub's `IFeedbackParamsDto`. Called for real on the
 *   Efkt path; 404s today.
 * - **`canReceiveFeedback`** (`./orders/helpers.ts`). The gateway's order-lines read never returns the
 *   field, so it resolves `false` and the Efkt tree cannot offer the "give feedback" entry point.
 *   Same BE ticket as the row above.
 * - **File uploads** (`./assets`). The multipart saga is wired against the *real* backend contract and
 *   is chunked — it is not a mock. Two BE surfaces are missing underneath it: no `/api/assets/uploads/*`
 *   route on the fe-gateway, and no HTTP way to create the asset row that `initiate` requires. See the
 *   class doc on `AssetsApi` and the note on `AssetsApi.createAsset`.
 *
 * Everything else missing from Efkt is either genuinely mocked (grep `EFKT-MOCK` across this package
 * for the full list, each with its own reasoning) or deliberately out of scope — see the per-page
 * tickets under `booqly-ui/docs/jira/` for the BE-facing contract lists, and the plan history for why
 * Google Analytics tracking isn't here at all.
 *
 * `support` used to be listed here as a permanent RC-served decision — help articles are CMS content,
 * not per-record migrated data, so there was thought to be nothing to mirror. `docs/jira/migrate-support-page.md`
 * still carries that original reasoning. It's mocked now anyway (`./support`), same EFKT-MOCK idiom as
 * everything else, so a future real endpoint needs no FE change — see that file's own banner comment.
 */
export const setEfktApiErrorHandler = http.setErrorHandler;
// Export common types
export * from './types';
// Export models
export * from './address/models';
export * from './assets/models';
export * from './branches/models';
export * from './cases/models';
export * from './contacts/models';
export * from './catalogue/models';
export * from './orders/models';
export * from './material/models';
export * from './activities/models';
export * from './bookings/models';
export * from './news/models';
export * from './notes/models';
export * from './support/models';
export * from './tasks/models';
export * from './texts/models';
export * from './users/models';
export * from './auth/models';
// Export helpers
export { isAddressComplete, mapAddressFromDto, mapAddressToDto } from './address/helpers';
export { mapContactToContactPerson } from './contacts/helpers';
export { getConfirmOrderProblems, mapOrderStatusFromDto } from './orders/helpers';
export { mapTaskCategoryToOrderTaskDto } from './tasks/helpers';
// Export apis
export { default as AssetsApi } from './assets';
export { default as BranchesApi } from './branches';
export { default as CasesApi } from './cases';
export { default as ContactsApi } from './contacts';
export { default as CatalogueApi } from './catalogue';
export { default as OrdersApi } from './orders';
export { default as MaterialApi } from './material';
export { default as BookingsApi } from './bookings';
export { default as NewsApi } from './news';
export { default as NotesApi } from './notes';
export { default as SupportApi } from './support';
export { default as TasksApi } from './tasks';
export { default as TextsApi } from './texts';
export { default as UsersApi } from './users';
export { default as AuthApi } from './auth';
export { endpoints };
